import {doseCalculationMethodOptions} from '@/modules/drug-catalog/constants'
import {useGetDrugCatalogItem} from '@/modules/drug-catalog/substances/api'
import {useGetPrescribedName} from '@/modules/products/trade-names/api'
import {changeDoseUnitLabel, getDrugDetails} from '@/modules/protocols/functions'
import {DrugLimitCheckResult} from '@/shared/contexts'
import {Box, NumberInput, Separator, Text} from '@chakra-ui/react'
import {DoseCalcModeActive} from '@cypro/engine/database/schema'
import {PatientCharacteristic, PatientGfr, PatientTherapySequenceMedicine} from '@cypro/engine/schema'
import {getDosingWeight, getTotalAmountOfSubstanceDecimal} from '@cypro/engine/utils'
import {withFieldGroup} from '@cypro/ui/integrations/form'
import {multipleDigitsNumberFormatter} from '@cypro/ui/utils'
import {useStore} from '@tanstack/react-form'
import {T, useTranslate} from '@tolgee/react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {LuCircleAlert} from 'react-icons/lu'
import {ActiveSubstanceSelect} from './active-substance-select'
import {DrugFormOptions} from './drug-form-options'
import {PrescribedNameSelect} from './prescribed-name-select'
import {doseFieldsChanged} from './drug-form-total'

export const DrugFormNew = withFieldGroup({
  ...DrugFormOptions,
  props: {} as {
    hasMultiple: boolean
    index: number
    usePrescribedName?: boolean
    pharmacistValidation?: boolean
    savedMedicine?: PatientTherapySequenceMedicine | null
    disabled?: boolean
    // a doctor validated the clinical content, so the dose fields cannot be changed
    lockedClinicalFields?: boolean
    /** The form's current No Aut Idem value; when set the doctor fixed the product, so it cannot be substituted. */
    autIdem?: boolean
    /** Show the stored total instead of recalculating it from the patient's current weight/BSA/GFR. */
    useSavedTotalAmount?: boolean
    characteristics?: PatientCharacteristic
    gfr?: PatientGfr
    limits: DrugLimitCheckResult[]
  },
  render: ({
    group,
    hasMultiple,
    index,
    gfr,
    characteristics,
    usePrescribedName,
    pharmacistValidation,
    savedMedicine,
    disabled,
    lockedClinicalFields,
    useSavedTotalAmount,
    autIdem,
    limits,
  }) => {
    const {t} = useTranslate('common')
    const [totalAmount, setTotalAmount] = useState('0')
    const calculationDoseItems = doseCalculationMethodOptions.map(value => ({label: t(value), value}))
    const calculationMethod = useStore(group.store, state => state.values.calculationMethod)
    const prescribedNameId = useStore(group.store, state => state.values.prescribedNameId)
    const [prescribedName] = useGetPrescribedName(prescribedNameId)
    const activesustanceId = useStore(group.store, state => state.values.activeSubstanceId)
    const [activeSubstance] = useGetDrugCatalogItem(activesustanceId)
    const medicineValues = useStore(group.store, state => state.values)
    const unitLabel = getDrugDetails(activeSubstance)?.doseUnit?.displayName
    const doseUnit = calculationMethod ? changeDoseUnitLabel(calculationMethod, activeSubstance, unitLabel) : unitLabel
    const displayedLimit = limits && limits?.filter(item => item.substanceId === activeSubstance?.id)

    useEffect(() => {
      setTotalAmount('0')
    }, [usePrescribedName])

    const updateTotalAmount = useCallback(
      (calculation?: DoseCalcModeActive) => {
        if (calculation || calculationMethod) {
          const {height, bsa} = characteristics || {}
          const weight = getDosingWeight(characteristics)
          const {gfr: patientGfr} = gfr || {}
          const details = getDrugDetails(activeSubstance)
          const amount = getTotalAmountOfSubstanceDecimal({
            calculationMethod: calculation || calculationMethod || 'ABSOLUTE',
            dose: group.getFieldValue('regularDose'),
            doseProportion: group.getFieldValue('doseProportion'),
            roundingMode: details?.roundingMode,
            dosePrecision: group.getFieldValue('precisionDecimals'),
            height,
            weight,
            gfr: patientGfr,
            bsa,
          }).toString()
          setTotalAmount(amount)
        }
      },
      [group, activeSubstance, calculationMethod, characteristics, gfr]
    )

    // a doctor may prescribe the substance without a trade name; keep that substance when the dialog opens
    const savedSubstanceId = savedMedicine?.activeSubstanceId
    const setMedicineField = useCallback(
      (name: string, value: unknown, options?: {dontRunListeners?: boolean}) => {
        group.form.setFieldValue(`medicine[${index}].${name}`, value, {dontUpdateMeta: true, ...options})
      },
      [group, index]
    )

    useEffect(() => {
      if (!savedSubstanceId || group.getFieldValue('activeSubstanceId')) return
      setMedicineField('activeSubstanceId', savedSubstanceId, {dontRunListeners: true})
    }, [group, savedSubstanceId, setMedicineField])

    const prescribedNameInitialisedRef = useRef(false)

    useEffect(() => {
      if (!usePrescribedName) return
      // keep the saved substance on open: a doctor may prescribe the substance without a trade name
      if (!prescribedNameInitialisedRef.current) {
        prescribedNameInitialisedRef.current = true
        return
      }

      if (prescribedName?.drugCatalogItemId) {
        setMedicineField('activeSubstanceId', prescribedName.drugCatalogItemId, {dontRunListeners: true})
      } else {
        setMedicineField('activeSubstanceId', undefined, {dontRunListeners: true})
      }
    }, [prescribedName, usePrescribedName, setMedicineField])

    const autoFilledSubstanceIdRef = useRef<string | undefined>(group.getFieldValue('activeSubstanceId'))

    useEffect(() => {
      if (!activeSubstance) return

      const details = getDrugDetails(activeSubstance)
      if (!details?.doseCalcMode || autoFilledSubstanceIdRef.current === activeSubstance.id) return

      autoFilledSubstanceIdRef.current = activeSubstance.id
      setMedicineField('calculationMethod', details.doseCalcMode)
      // the catalog's dose precision decides how many decimals the stored dose keeps
      if (details.dosePrecision !== undefined && details.dosePrecision !== null) {
        setMedicineField('precisionDecimals', details.dosePrecision)
      }
    }, [activeSubstance, setMedicineField])

    // a doctor-validated prescription shows the total it was signed off with until a dose field changes
    const doseEdited =
      Boolean(savedMedicine) &&
      doseFieldsChanged(medicineValues, {
        activeSubstanceId: savedMedicine?.activeSubstanceId,
        calculationMethod: savedMedicine?.calculationMethod,
        regularDose: savedMedicine?.regularDose,
        doseProportion: savedMedicine?.doseProportion,
        precisionDecimals: savedMedicine?.precisionDecimals,
      })

    useEffect(() => {
      if (useSavedTotalAmount && savedMedicine?.totalAmount != null && !doseEdited) {
        setTotalAmount(String(savedMedicine.totalAmount))
        return
      }

      updateTotalAmount()
    }, [doseEdited, savedMedicine?.totalAmount, useSavedTotalAmount, usePrescribedName, updateTotalAmount])

    return (
      <>
        <Box display={'flex'} flexDirection={'column'} gap="3" width="100%">
          <Box display={'flex'} flexDirection={hasMultiple ? 'column' : 'row'} gap={hasMultiple ? 4 : 3} width="100%">
            {usePrescribedName ? (
              <PrescribedNameSelect
                // @ts-expect-error
                form={group.form}
                hasMultiple={hasMultiple}
                index={index}
                pharmacistValidation={pharmacistValidation}
                savedMedicine={savedMedicine}
                disabled={disabled}
                autIdem={autIdem}
              />
            ) : (
              <ActiveSubstanceSelect
                // @ts-expect-error
                form={group.form}
                hasMultiple={hasMultiple}
                index={index}
                disabled={disabled || lockedClinicalFields}
              />
            )}

            <group.AppField
              name={`calculationMethod`}
              mode="value"
              listeners={{
                onChange: ({value}) => {
                  updateTotalAmount(value)
                },
              }}
            >
              {field => (
                <field.Select
                  size="sm"
                  label={t('therapy_sequence.medicine.calculation_based_on.label')}
                  placeholder={t('select')}
                  items={calculationDoseItems}
                  portalled
                  required
                  disabled={!activeSubstance || disabled || lockedClinicalFields}
                  controlProps={{
                    bgColor: 'white',
                  }}
                />
              )}
            </group.AppField>
          </Box>

          <Box display={'flex'} flexDirection={hasMultiple ? 'column' : 'row'} gap="4" width="100%">
            <Box display="flex" flexDirection="row" gap="4" width="100%">
              <group.AppField
                name={`regularDose`}
                listeners={{
                  onMount: () => {
                    updateTotalAmount()
                  },
                  onChange: () => {
                    updateTotalAmount(calculationMethod)
                  },
                }}
              >
                {field => (
                  <field.NumberInput
                    size="sm"
                    label={`${t('regular_dose')} ${doseUnit ? `(${doseUnit})` : ''}`}
                    placeholder="0"
                    min={0}
                    required
                    disabled={!activeSubstance || disabled || lockedClinicalFields}
                  />
                )}
              </group.AppField>{' '}
              <group.AppField
                name={`precisionDecimals`}
                listeners={{
                  onChange: () => {
                    updateTotalAmount(calculationMethod)
                  },
                }}
              >
                {field => (
                  <field.NumberInput
                    size="sm"
                    label={t('precision_decimals')}
                    placeholder="0"
                    min={0}
                    max={100}
                    disabled={!activeSubstance || disabled || lockedClinicalFields}
                  />
                )}
              </group.AppField>
            </Box>
            <Box display="flex" flexDirection="row" gap="4" width="100%">
              <group.AppField
                name={`doseProportion`}
                listeners={{
                  onChange: () => {
                    updateTotalAmount(calculationMethod)
                  },
                }}
              >
                {field => (
                  <field.NumberInput
                    size="sm"
                    label={t('therapy_sequence.medicine.dose_proportion.label')}
                    placeholder={t('therapy_sequence.medicine.dose_proportion.placeholder')}
                    min={1}
                    max={100}
                    disabled={!activeSubstance || disabled || lockedClinicalFields}
                  />
                )}
              </group.AppField>
              <NumberInput.Root
                size="sm"
                width="100%"
                value={totalAmount}
                formatOptions={{style: 'decimal'}}
                disabled={disabled}
              >
                <NumberInput.Label>
                  <Text fontWeight="medium" fontSize={'sm'} opacity={disabled ? '0.5' : '1'}>
                    {`${t('therapy_sequence.medicine.total_amount')} ${unitLabel ? `(${unitLabel})` : ''}`}
                  </Text>
                </NumberInput.Label>
                <NumberInput.Input
                  placeholder={t('therapy_sequence.medicine.dose_proportion.placeholder')}
                  disabled
                  marginTop={2}
                />
              </NumberInput.Root>
            </Box>
          </Box>
          {displayedLimit && displayedLimit[0] && (
            <Box
              key={displayedLimit[0].substanceId}
              bgColor={'orange.subtle'}
              display={'flex'}
              padding="4"
              gap="3"
              width={'100%'}
              colorPalette={'orange.fg'}
            >
              <Box colorPalette={'orange.fg'}>
                <LuCircleAlert size="18" color="#92310A" />
              </Box>
              <Box>
                <Text fontWeight={'medium'} color="orange.fg">
                  {t('overdose_limit_warning_title', {
                    limitType: displayedLimit[0].level,
                  })}
                </Text>
                <Text fontSize="sm" color="orange.fg" fontWeight={'normal'}>
                  <T
                    keyName="overdose_limit_warning"
                    params={{
                      name: displayedLimit[0].substanceName,
                      limitType: displayedLimit[0].level,
                      limit: multipleDigitsNumberFormatter.format(displayedLimit[0].limit),
                      unit: displayedLimit[0].unit,
                      b: <Text as="span" fontWeight="bold" />,
                    }}
                  />
                </Text>
              </Box>
            </Box>
          )}
        </Box>
        {index === 0 && hasMultiple && <Separator orientation="vertical" background="border" />}
      </>
    )
  },
})
